// Ops-reviewer variant of salesIntake — paperwork-check queue for incoming
// ops_order_intake rows. Distinct from post-acceptance deal management in
// Ops → operations (turbo_sales.md §5 item 4).

import { PaginationBar } from "@/components/common/PaginationBar";
import type { PageSize } from "@/components/common/usePagination";
import { useAuth } from "@/contexts/AuthContext";
import { ghostBtnClass } from "@/components/manufacturing/ManufacturingOrderIntakeShared";
import type { OpsOrderIntakeRow } from "@/lib/orderIntakeClient";
import { OPS_REVIEWER_ROLES } from "@/lib/roles/turboOpsRoles";
import { useState } from "react";

import { IdeaRoomCustomerInbox } from "./IdeaRoomCustomerInbox";
import { OrderIntakeReviewDetailDrawer } from "./OrderIntakeReviewDetailDrawer";
import { OrderIntakeRowList, OrderIntakeTabBar } from "./orderIntakeReviewList";
import { useOrderIntakeReview, type IntakePageSize } from "./useOrderIntakeReview";

export function OpsReviewerIntakePanel() {
  const auth = useAuth();
  const allowed = auth.hasRole(...OPS_REVIEWER_ROLES);
  const review = useOrderIntakeReview({ paginate: true });
  const [viewMode, setViewMode] = useState<"customers" | "orders">("customers");
  const [customerIntake, setCustomerIntake] = useState<OpsOrderIntakeRow | null>(null);
  const [customerRefreshKey, setCustomerRefreshKey] = useState(0);
  const selectedRow = customerIntake ?? review.selectedRow;

  const closeDetail = () => {
    setCustomerIntake(null);
    review.setSelectedId(null);
  };

  if (!allowed) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100">
        Ops review requires an <code>ops_reviewer</code> role token (or an umbrella
        token that implies it — see <code>docs/design/role_taxonomy.md</code>).
      </div>
    );
  }

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">
            Ops review — Order Intake
          </h2>
          <p className="text-xs text-gray-600 dark:text-zinc-400">
            Paperwork-check queue for incoming orders. Accept when the snapshot looks
            complete; Hold to request fixes; Cancel to soft-archive. Post-acceptance
            deal paperwork lives in Ops → <strong>Deals</strong> (<code>operations</code>).
          </p>
        </div>
        {viewMode === "orders" ? (
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => void review.refresh()}
            disabled={review.loadingRows || review.actionBusy}
          >
            {review.loadingRows ? "Refreshing…" : "Refresh"}
          </button>
        ) : null}
      </div>

      <OrderIntakeTabBar
        activeTab={review.activeTab}
        onChange={(tab) => {
          closeDetail();
          review.setActiveTab(tab);
        }}
        total={review.total}
      />

      <div className="flex gap-2" role="group" aria-label="Intake view">
        {(["customers", "orders"] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => {
              closeDetail();
              setViewMode(mode);
            }}
            className={
              "rounded-md px-3 py-1.5 text-sm font-medium " +
              (viewMode === mode
                ? "bg-gray-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                : "bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-zinc-200")
            }
          >
            {mode === "customers" ? "Customers" : "Orders"}
          </button>
        ))}
      </div>

      {viewMode === "orders" && review.listError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {review.listError}
        </div>
      ) : null}

      {viewMode === "customers" ? (
        <IdeaRoomCustomerInbox
          key={`${review.activeTab}:${customerRefreshKey}`}
          activeTab={review.activeTab}
          onInitialAvailability={(available) => {
            if (review.activeTab === "pending_review" && !available) setViewMode("orders");
          }}
          onOpenIntake={(intake) => {
            setCustomerIntake(intake);
            review.setSelectedId(intake.id);
          }}
        />
      ) : (
        <>
          <OrderIntakeRowList
            rows={review.rows}
            loading={review.loadingRows}
            activeTab={review.activeTab}
            selectedId={review.selectedId}
            onSelect={(id) => {
              setCustomerIntake(null);
              review.setSelectedId(id);
            }}
            title={review.activeTab === "pending_review" ? "Pending review" : "Queue"}
          />
          <PaginationBar
            pageIndex={review.pageIndex}
            pageCount={review.pageCount}
            pageSize={review.pageSize as PageSize}
            totalCount={review.total ?? 0}
            firstShown={review.firstShown}
            lastShown={review.lastShown}
            onPageChange={review.setPageIndex}
            onPageSizeChange={(n) => {
              if ((review.pageSizeOptions as readonly number[]).includes(n)) {
                review.setPageSize(n as IntakePageSize);
              }
            }}
            pageSizeOptions={review.pageSizeOptions}
            noun="orders"
            alwaysShow
          />
        </>
      )}

      {selectedRow ? (
        <OrderIntakeReviewDetailDrawer
          row={selectedRow}
          variant="ops_reviewer"
          onClose={closeDetail}
          actionBusy={review.actionBusy}
          actionError={review.actionError}
          onAccept={async (plantOverride) => {
            const ok = await review.acceptSelected(plantOverride);
            if (ok) {
              setCustomerIntake(null);
              setCustomerRefreshKey((value) => value + 1);
            }
            return ok;
          }}
          onHold={async (reason) => {
            const ok = await review.holdSelected(reason);
            if (ok) {
              setCustomerIntake(null);
              setCustomerRefreshKey((value) => value + 1);
            }
            return ok;
          }}
          onCancel={async () => {
            const ok = await review.cancelSelected();
            if (ok) {
              setCustomerIntake(null);
              setCustomerRefreshKey((value) => value + 1);
            }
            return ok;
          }}
          onReturnToHold={async (reason) => {
            const ok = await review.returnSelectedToHold(reason);
            if (ok) {
              setCustomerIntake(null);
              setCustomerRefreshKey((value) => value + 1);
            }
            return ok;
          }}
        />
      ) : viewMode === "orders" && review.activeTab === "pending_review" && !review.loadingRows && review.rows.length > 0 ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Select a row above to run the paperwork check and choose Accept, Hold, or Cancel.
        </p>
      ) : null}
    </div>
  );
}
